/**
 * Natural cubic spline interpolation.
 * Returns an evaluator function that computes the interpolated value at any x.
 * Requires xs.length >= 2 and xs sorted ascending.
 */
export function buildCubicSpline(
  xs: number[],
  ys: number[],
): (x: number) => number {
  const n = xs.length;
  const h = new Array<number>(n - 1);
  for (let i = 0; i < n - 1; i++) {
    h[i] = xs[i + 1] - xs[i];
  }

  // Solve tridiagonal system for second derivatives c[]
  const alpha = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    alpha[i] =
      (3 / h[i]) * (ys[i + 1] - ys[i]) - (3 / h[i - 1]) * (ys[i] - ys[i - 1]);
  }

  const l = new Array<number>(n).fill(0);
  const mu = new Array<number>(n).fill(0);
  const z = new Array<number>(n).fill(0);
  l[0] = 1;

  for (let i = 1; i < n - 1; i++) {
    l[i] = 2 * (xs[i + 1] - xs[i - 1]) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / l[i];
    z[i] = (alpha[i] - h[i - 1] * z[i - 1]) / l[i];
  }

  const c = new Array<number>(n).fill(0);
  const b = new Array<number>(n).fill(0);
  const d = new Array<number>(n).fill(0);

  for (let j = n - 2; j >= 0; j--) {
    c[j] = z[j] - mu[j] * c[j + 1];
    b[j] = (ys[j + 1] - ys[j]) / h[j] - (h[j] * (c[j + 1] + 2 * c[j])) / 3;
    d[j] = (c[j + 1] - c[j]) / (3 * h[j]);
  }

  return function splineEval(x: number): number {
    if (x <= xs[0]) {
      return ys[0];
    }
    if (x >= xs[n - 1]) {
      return ys[n - 1];
    }

    let lo = 0;
    let hi = n - 2;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (xs[mid + 1] < x) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }

    const dx = x - xs[lo];
    return b[lo] * dx + c[lo] * dx * dx + d[lo] * dx * dx * dx + ys[lo];
  };
}
